/**
 * GscImportModal — shared modal for pulling top queries from
 * Search Console into seo_keyword.
 *
 * Used by:
 *   - IntegrationsPage  ("Import keywords" button on the GSC card)
 *   - SeoToolsPage      ("Import from Search Console" tile)
 *   - SeoKeywordsPage   ("Import from GSC" header button)
 *
 * Two-step UX:
 *   1. Customer adjusts knobs (top N, lookback days, sort) and hits
 *      "Preview". We GET /gsc/preview which doesn't write anything.
 *   2. Customer reviews the table → hits "Import N keywords".
 *      We POST /gsc/import which upserts into seo_keyword.
 *
 * Defaults (per the user's design pick):
 *   - sort_by = impressions desc
 *   - days = 28
 *   - top_n = 50
 *   - no position cap
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import {
  MdClose, MdSearch, MdDownload, MdCheckCircle, MdError,
} from 'react-icons/md';

import { previewGscImport, importGscKeywords } from '../../../../api/gscRequests';

// ---------------- styled ----------------

const Backdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, .55);
  display: grid; place-items: center;
  z-index: 999;
  padding: 1rem;
`;
const Modal = styled.div`
  background: #fff;
  width: min(720px, 100%);
  max-height: 90vh;
  border-radius: 14px;
  display: flex; flex-direction: column;
  overflow: hidden;
`;
const Head = styled.header`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex; align-items: center; gap: .75rem;
  h2 { margin: 0; font-size: 1.05rem; }
  .x {
    margin-left: auto;
    border: 0; background: transparent; font-size: 1.4rem;
    cursor: pointer; color: #64748b;
  }
`;
const Body = styled.div`
  padding: 1rem 1.25rem;
  overflow-y: auto;
`;
const Knobs = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: .75rem;
  margin-bottom: 1rem;
  label {
    display: block;
    font-size: .78rem; color: #64748b; margin-bottom: .25rem;
  }
  input, select {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: .45rem .55rem;
    font-size: .9rem;
  }
`;
const Btn = styled.button`
  border: 1px solid ${p => p.$primary ? '#4f46e5' : '#cbd5e1'};
  background: ${p => p.$primary ? '#4f46e5' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#0f172a'};
  padding: .5rem 1rem;
  font-size: .88rem; font-weight: 600;
  border-radius: 9px;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: .35rem;
  &:hover { filter: brightness(.95); }
  &:disabled { opacity: .55; cursor: not-allowed; }
`;
const Foot = styled.footer`
  padding: .85rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  display: flex; gap: .55rem; justify-content: flex-end;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: .85rem;
  th, td {
    padding: .45rem .55rem;
    border-bottom: 1px solid #f1f5f9;
    text-align: left;
  }
  th { color: #64748b; font-weight: 600; font-size: .78rem; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
`;
const Banner = styled.div`
  padding: .65rem .8rem;
  border-radius: 8px;
  font-size: .85rem;
  margin-bottom: .75rem;
  display: flex; align-items: center; gap: .45rem;
  &.err { background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca; }
  &.ok  { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  &.info{ background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
`;

// ---------------- main ----------------

export default function GscImportModal({ onClose, onDone }) {
  // knobs
  const [days, setDays]         = useState(28);
  const [topN, setTopN]         = useState(50);
  const [sortBy, setSortBy]     = useState('impressions');

  // state
  const [loading, setLoading]   = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview]   = useState(null);
  const [error, setError]       = useState(null);
  const [doneMsg, setDoneMsg]   = useState(null);

  const handlePreview = async () => {
    setError(null);
    setDoneMsg(null);
    setLoading(true);
    try {
      const r = await previewGscImport({
        days, top_n: topN, sort_by: sortBy,
      });
      setPreview(r?.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setError(null);
    setImporting(true);
    try {
      const r = await importGscKeywords({
        days, top_n: topN, sort_by: sortBy,
      });
      const data = r?.data || {};
      const msg = `Imported. +${data.added || 0} new keywords · ${data.updated || 0} enriched.`;
      setDoneMsg(msg);
      // Tell caller to refresh whatever it shows.
      if (onDone) onDone(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Backdrop onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal>
        <Head>
          <MdDownload style={{ color: '#4f46e5', fontSize: '1.4rem' }} />
          <h2>Import keywords from Search Console</h2>
          <button className="x" onClick={onClose}><MdClose /></button>
        </Head>

        <Body>
          {error && <Banner className="err"><MdError /> {error}</Banner>}
          {doneMsg && <Banner className="ok"><MdCheckCircle /> {doneMsg}</Banner>}
          {!preview && !error && !doneMsg && (
            <Banner className="info">
              Preview shows what we'd pull. Nothing is written until you
              click "Import".
            </Banner>
          )}

          <Knobs>
            <div>
              <label>Top N queries</label>
              <input
                type="number" min={5} max={1000} step={5}
                value={topN}
                onChange={(e) => setTopN(Math.max(1, Math.min(1000, +e.target.value || 50)))}
              />
            </div>
            <div>
              <label>Lookback (days)</label>
              <input
                type="number" min={1} max={180} step={1}
                value={days}
                onChange={(e) => setDays(Math.max(1, Math.min(180, +e.target.value || 28)))}
              />
            </div>
            <div>
              <label>Sort by (descending)</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="impressions">Impressions</option>
                <option value="clicks">Clicks</option>
                <option value="position">Best position</option>
                <option value="ctr">CTR</option>
              </select>
            </div>
          </Knobs>

          {preview && (
            <>
              <div style={{ fontSize: '.82rem', color: '#64748b', marginBottom: '.4rem' }}>
                Period: {preview.period_start} → {preview.period_end} · {preview.total_rows} rows · sort: {preview.sort_by}
              </div>
              <Table>
                <thead>
                  <tr>
                    <th>Query</th>
                    <th className="num">Impressions</th>
                    <th className="num">Clicks</th>
                    <th className="num">CTR</th>
                    <th className="num">Position</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.queries || []).slice(0, 50).map((q) => (
                    <tr key={q.query}>
                      <td>{q.query}</td>
                      <td className="num">{q.impressions.toLocaleString()}</td>
                      <td className="num">{q.clicks.toLocaleString()}</td>
                      <td className="num">{(q.ctr * 100).toFixed(1)}%</td>
                      <td className="num">{q.position.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {(preview.queries || []).length > 50 && (
                <div style={{ marginTop: '.5rem', fontSize: '.78rem', color: '#94a3b8' }}>
                  Showing first 50; full {preview.total_rows} will be imported.
                </div>
              )}
            </>
          )}
        </Body>

        <Foot>
          <Btn onClick={onClose}>Close</Btn>
          <Btn onClick={handlePreview} disabled={loading || importing}>
            <MdSearch /> {loading ? 'Loading…' : 'Preview'}
          </Btn>
          <Btn $primary onClick={handleImport} disabled={importing}>
            <MdDownload /> {importing ? 'Importing…' : (preview ? `Import ${preview.total_rows} keywords` : 'Import')}
          </Btn>
        </Foot>
      </Modal>
    </Backdrop>
  );
}
